/**
 * ContractorFormModal — formatka kontrahenta (create/edit) osadzona w oknie modalnym.
 *
 * Cel (spotkanie FK 31.08, „okno wyboru kontrahenta"): dodać nowego kontrahenta lub
 * edytować wybranego BEZ wychodzenia z okna wyboru na dokumencie. Zamiast przepisywać
 * pełną formatkę (ContractorDetail, ~1300 linii: layout, pola dodatkowe, tabele
 * dodatkowe, kontrola kartoteki, MF/NIP, adres macierzysty) reużywamy JĄ w trybie
 * osadzonym (`ContractorDetail embedded={…}`) — jedno źródło prawdy formatki dla strony
 * i okna. Modal daje tylko ramę i tytuł; przyciski Zapisz/Anuluj/Usuń są w formatce.
 *
 * `ContractorDetail` mountuje się dopiero gdy Modal jest otwarty (Modal zwraca `null`
 * przy `open={false}`), więc każde otwarcie startuje na czystym stanie (create = pusty
 * formularz, edit = świeży fetch rekordu).
 *
 * `onSaved(id)` woła się po udanym zapisie (create → id nowego rekordu, edit → id
 * edytowanego). Okno-gospodarz (picker) decyduje, co dalej — zwykle: zamknąć modal i
 * zaznaczyć/odświeżyć kontrahenta. W trybie create zapis zamyka okno (nowy rekord =
 * jasny sygnał wyboru); w trybie edit zamknięcie zostawiamy użytkownikowi (może
 * edytować dalej inne zakładki), więc `onSaved` NIE zamyka modala samo z siebie.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Modal';
import { ContractorDetail } from '@/pages/ContractorDetail';

export interface ContractorFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** Wymagane w trybie edit — kontrahent do edycji. */
  contractorId?: string;
  /** Firma-scope dokumentu (prefill właściciela w create, fallback firmy w edit). */
  companyId?: string;
  onClose: () => void;
  /** Po udanym zapisie — `id` zapisanego rekordu (create i edit). */
  onSaved: (id: string) => void;
  'data-testid'?: string;
}

export function ContractorFormModal({
  open,
  mode,
  contractorId,
  companyId,
  onClose,
  onSaved,
  'data-testid': testId,
}: ContractorFormModalProps) {
  const { t } = useTranslation();
  // Edycja zostaje otwarta po zapisie — zapis zeruje „coś wpisano", żeby Esc zaraz po zapisie
  // nie pytał o porzucenie zmian, które już zapisano.
  const [savedRev, setSavedRev] = useState(0);
  const handleSaved = (id: string) => {
    setSavedRev((rev) => rev + 1);
    onSaved(id);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="2xl"
      fixedHeight
      expandable
      guardUnsaved
      guardResetKey={savedRev}
      title={
        mode === 'create'
          ? t('tenant.contractors.formModal.createTitle')
          : t('tenant.contractors.formModal.editTitle')
      }
    >
      <div data-testid={testId ?? 'contractor-form-modal'}>
        <ContractorDetail
          embedded={{ mode, contractorId, companyId, onSaved: handleSaved, onCancel: onClose }}
        />
      </div>
    </Modal>
  );
}
